// Query-string ownership for the app shell. Three kinds of keys:
//  - SHELL keys select the chat context (esp. conversationId, which selects
//    the visible thread). They ride along on cross-tab navigation.
//  - EPHEMERAL keys are transient UI state (which mobile drawer is open).
//    They are never carried across tasks and never stored on a tab —
//    preserving mobilePanel would keep the full-screen chat drawer over a
//    tapped entity.
//  - Everything else is task-owned and belongs to exactly one tab.

// The task-owned sibling key `conversation` (query-results) is deliberately
// NOT a shell key — renaming either breaks backgrounded results tabs.
const SHELL_KEYS = ['agent', 'conversationId', 'view'] as const

/**
 * App-wide nuqs defaults — pass to the single `<NuqsAdapter>` in App.tsx.
 *
 * nuqs defaults to SHALLOW updates: it calls history.pushState/replaceState
 * itself and does NOT tell react-router. useLocation never changes, so
 * AppShellEffects never sees the new query and never records it on the tab —
 * every filter written through nuqs is lost on the next tab switch.
 * `shallow: false` makes nuqs also navigate(), so the shell sees the write.
 *
 * Per-hook options still win, so keep passing `shallow` nowhere unless a param
 * is deliberately invisible to the router.
 */
export const NUQS_SHELL_DEFAULT_OPTIONS = { shallow: false } as const

const EPHEMERAL_KEYS = ['mobilePanel'] as const

const SHELL_KEY_SET: ReadonlySet<string> = new Set<string>(SHELL_KEYS)

const DROPPED_KEYS: ReadonlySet<string> = new Set<string>([
  ...SHELL_KEYS,
  ...EPHEMERAL_KEYS,
])

/** The raw '&'-separated pairs of a search string, empty ones dropped. */
function pairsOf(search: string): string[] {
  return search.replace(/^\?/, '').split('&').filter(Boolean)
}

/** The DECODED key of one raw pair — undefined when the pair has no key. */
function keyOf(pair: string): string | undefined {
  // URLSearchParams decodes '+' and '%XX' without throwing on a malformed
  // escape, unlike decodeURIComponent.
  const [key] = new URLSearchParams(pair).keys()
  return key
}

/**
 * The task-owned part of a search string: shell + ephemeral keys removed.
 * Returns '' when nothing is left, otherwise a leading-'?' string.
 *
 * Filters the RAW '&'-separated pairs and keeps the survivors byte-identical.
 * A round trip through URLSearchParams.toString() re-encodes them ('%20'
 * becomes '+'), so the href stored on a tab would no longer match the URL the
 * user is on. Only the KEY is decoded, to compare it against the drop list.
 */
export function taskSearchOf(search: string): string {
  const kept = pairsOf(search).filter((pair) => {
    const key = keyOf(pair)
    return key === undefined || !DROPPED_KEYS.has(key)
  })
  return kept.length > 0 ? `?${kept.join('&')}` : ''
}

/**
 * Merge only the missing shell keys from the current URL into the target's
 * own query — the target's own params always win. Ephemeral keys are never
 * copied. Returns '' when nothing is left, otherwise a leading-'?' string.
 *
 * Filters RAW pairs like taskSearchOf: both the target's pairs and the copied
 * shell pairs keep their original bytes and order. A tab href built here has
 * to compare equal to the URL the browser shows, and URLSearchParams.toString()
 * would re-encode it.
 */
export function mergeShellSearch(
  targetSearch: string,
  currentSearch: string
): string {
  const kept = pairsOf(targetSearch)
  const seen = new Set(kept.map(keyOf))
  for (const pair of pairsOf(currentSearch)) {
    const key = keyOf(pair)
    if (key === undefined || !SHELL_KEY_SET.has(key) || seen.has(key)) continue
    // Copy the FIRST occurrence only, as URLSearchParams.get() did.
    seen.add(key)
    kept.push(pair)
  }
  return kept.length > 0 ? `?${kept.join('&')}` : ''
}

/**
 * Merge EVERY key of the current search that the target has not already set —
 * the same-tab counterpart of mergeShellSearch, which copies only shell keys.
 * The target's own params always win. Returns '' when nothing is left,
 * otherwise a leading-'?' string.
 */
export function mergeSearch(
  targetSearch: string,
  currentSearch: string
): string {
  if (!targetSearch) return currentSearch
  const merged = new URLSearchParams(targetSearch)
  for (const [key, value] of new URLSearchParams(currentSearch)) {
    if (!merged.has(key)) merged.append(key, value)
  }
  const qs = merged.toString()
  return qs ? `?${qs}` : ''
}

/** Pathname part of an href-shaped string ('/a?x=1#h' → '/a'). */
export function pathnameOf(href: string): string {
  return href.split('?')[0]!.split('#')[0]!
}
